import { ReactElement } from 'react';
import { useTranslation } from 'next-i18next';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import Swiper from '~/ui/components/common/Swiper/Swiper';

const MobileSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$mobileSection' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$mobile' });

  const heroSlides = [
    {
      id: 1,
      description: t('$heroSlides.firstSlideDescription'),
      imageSrc: 'images/homePage/mobileSection/luxury-collection-chufy-peru-collection-mob.jpg',
      link: tLink('$heroSlides.firstSlideLink'),
    },
    {
      id: 2,
      description: t('$heroSlides.secondSlideDescription'),
      imageSrc:
        'images/homePage/discoverSection/luxury-collection-mercedes-salazar-home-collection.jpg',
      link: tLink('$heroSlides.secondSlideLink'),
    },
    {
      id: 3,
      description: t('$heroSlides.thirdSlideDescription'),
      imageSrc: 'images/homePage/discoverSection/luxury-collection-assouline-collection.jpg',
      link: tLink('$heroSlides.thirdSlideLink'),
    },
    {
      id: 4,
      description: t('$heroSlides.fourthSlideDescription'),
      imageSrc: 'images/homePage/discoverSection/luxury-collection-chufy-kimono.jpg',
      link: tLink('$heroSlides.fourthSlideLink'),
    },
    {
      id: 5,
      description: t('$heroSlides.fifthSlideDescription'),
      imageSrc: 'images/homePage/discoverSection/luxury-collection-globe-trotter-luggage.jpg',
      link: tLink('$heroSlides.fifthSlideLink'),
    },
    {
      id: 6,
      description: t('$heroSlides.sixthSlideDescription'),
      imageSrc: 'images/homePage/discoverSection/luxury-collection-missoni-backpack.jpg',
      link: tLink('$heroSlides.sixthSlideLink'),
    },
  ];

  const beddingCarouselSlides = [
    {
      id: 1,
      title: t('$beddingCarouselSlides.firstSlideTitle'),
      altTitle: t('$beddingCarouselSlides.firstSlideAltTitle'),
      imageSrc: 'images/homePage/mobileSection/bedding-sets-hp-mob.jpg',
      link: tLink('$beddingCarouselSlides.firstSlideLink'),
    },
    {
      id: 2,
      title: t('$beddingCarouselSlides.secondSlideTitle'),
      altTitle: t('$beddingCarouselSlides.secondSlideAltTitle'),
      imageSrc: 'images/homePage/mobileSection/pillows-hp-mob.jpg',
      link: tLink('$beddingCarouselSlides.secondSlideLink'),
    },
    {
      id: 3,
      title: t('$beddingCarouselSlides.thirdSlideTitle'),
      altTitle: t('$beddingCarouselSlides.thirdSlideAltTitle'),
      imageSrc: 'images/homePage/mobileSection/linens-hp-mob.jpg',
      link: tLink('$beddingCarouselSlides.thirdSlideLink'),
    },
  ];

  const bathCarouselSlides = [
    {
      id: 1,
      title: t('$bathCarouselSlides.firstSlideTitle'),
      altTitle: t('$bathCarouselSlides.firstSlideAltTitle'),
      imageSrc: 'images/homePage/mobileSection/robes-hp-mob.jpg',
      link: tLink('$bathCarouselSlides.firstSlideLink'),
    },
    {
      id: 2,
      title: t('$bathCarouselSlides.secondSlideTitle'),
      altTitle: t('$bathCarouselSlides.secondSlideAltTitle'),
      imageSrc: 'images/homePage/mobileSection/towels-hp-mob.jpg',
      link: tLink('$bathCarouselSlides.secondSlideLink'),
    },
    {
      id: 3,
      title: t('$bathCarouselSlides.thirdSlideTitle'),
      altTitle: t('$bathCarouselSlides.thirdSlideAltTitle'),
      imageSrc: 'images/homePage/mobileSection/le-chemin-hp-mob.jpg',
      link: tLink('$bathCarouselSlides.thirdSlideLink'),
    },
  ];

  const renderHeroSlide = (slide: any, index: number): ReactElement => (
    <>
      <Link
        href={slide.link}
        noTabbing
        data-event-name="bloc_interact"
        data-bloc-name={slide.title}
      >
        <div className="image-container">
          <div
            role="img"
            aria-label={slide.title || slide.description}
            className={`bg-slide ${slide.title ? slide.title : ''}`}
            data-src={slide.imageSrc}
            style={{
              backgroundImage: `url(${slide.imageSrc})`,
            }}
          />
        </div>
      </Link>
      <p className="card-description">{slide.description}</p>
    </>
  );

  const renderCarouselSlide = (slide: any, index: number): ReactElement => (
    <Link href={slide.link} noTabbing data-event-name="bloc_interact" data-bloc-name={slide.title}>
      <div className="image-container">
        <img
          data-src={slide.imageSrc}
          alt={slide.altTitle}
          src={slide.imageSrc}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          width="648"
          height="450"
        />
      </div>
      <p className="card-title">{slide.title}</p>
    </Link>
  );

  const slideClassName = (): string => 'card';

  return (
    <div className="mobile-only">
      <section className="base-section hero-section main-hp" id="HP_hero_bed">
        <div className="mobile-only">
          <h2>
            {t('heroTitle')}
            <span>{t('heroTitleSpan')}</span>
          </h2>
          <div className="carousel-wrapper">
            <div className="swiper card-wrapper hp-swiper">
              <Swiper
                slides={heroSlides}
                renderSlide={renderHeroSlide}
                slideClassName={slideClassName}
                navigation // Include navigation
                pagination // Include pagination
                loop
                containerModifierClass="swiper-container-"
                className="swiper-container swiper-wrapper"
                // Default values - making mobile as default
                slidesPerView={1}
                slidesPerGroup={1} // Move 'X' slides at a time
                spaceBetween={0} // Add space between slides
                touchEventsTarget="container"
              />
            </div>
          </div>
          <div className="mobile-only hp-mobile-content">
            <h2 className="pattern">
              <span className="tagline-span">{t('tagline')}</span>
            </h2>
            <div className="hp-mobile-content-image">
              <picture>
                <source
                  srcSet="/images/homePage/mobileSection/bed-hero-hp-mob.jpg"
                  media="(max-width: 320px)"
                />
                <img
                  srcSet="/images/homePage/mobileSection/bed-hero-hp-mob.jpg"
                  alt="bed hero hp"
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  width="648"
                  height="648"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      <section className="bedding-carousel">
        <div className="bedding-carousel-wrapper">
          <div className="carousel-wrapper">
            <div className="swiper card-wrapper hp-swiper">
              <Swiper
                slides={beddingCarouselSlides}
                renderSlide={renderCarouselSlide}
                slideClassName={slideClassName}
                navigation // Include navigation
                pagination // Include pagination
                containerModifierClass="swiper-container-"
                className="swiper-container swiper-wrapper"
                // Default values - making mobile as default
                slidesPerView={1.1}
                slidesPerGroup={1} // Move 'X' slides at a time
                spaceBetween={15} // Add space between slides
                touchEventsTarget="container"
              />
            </div>
          </div>
          <div className="pattern" />
        </div>
      </section>
      <section className="bathspa-section">
        <div className="hp-mobile-content">
          <h2 className="pattern">{t('bathSpaTitle')}</h2>
          <div className="hp-mobile-content-image">
            <picture>
              <source
                srcSet="/images/homePage/mobileSection/bath-hero-hp-mob.jpg"
                media="(max-width: 320px)"
              />
              <img
                srcSet="/images/homePage/mobileSection/bath-hero-hp-mob.jpg"
                alt="bath hero hp"
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                width="648"
                height="648"
              />
            </picture>
          </div>
        </div>
      </section>
      <section className="bath-carousel">
        <div className="bath-carousel-wrapper">
          <div className="carousel-wrapper">
            <div className="swiper card-wrapper hp-swiper">
              <Swiper
                slides={bathCarouselSlides}
                renderSlide={renderCarouselSlide}
                slideClassName={slideClassName}
                navigation // Include navigation
                pagination // Include pagination
                containerModifierClass="swiper-container-"
                className="swiper-container swiper-wrapper"
                // Default values - making mobile as default
                slidesPerView={1.1}
                slidesPerGroup={1} // Move 'X' slides at a time
                spaceBetween={15} // Add space between slides
                touchEventsTarget="container"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobileSection;
